import React, { useEffect, useState } from "react"
import { useNavigate, useParams } from "react-router-dom"
import { Row, Col, Input, Button, Spinner, FormGroup, Label, FormFeedback, Alert } from "reactstrap"
import { useFormik } from "formik"
import * as Yup from "yup"
import { toast } from "react-toastify"
import {
  adminGetAddonModule,
  adminCreateAddonModule,
  adminUpdateAddonModule,
  adminGetAddonFeatures,
} from "../../api/requests/addon-requests"
import { adminGetPlanIntegrations } from "../../api/requests/subscription-requests"
import BackButton from "../../components/Common/BackButton"

const NAVY = "#221C5D"

/**
 * Doğrulama hatasında kullanıcıya hangi alandan bahsettiğimizi söyleyebilmek için
 * teknik alan adının karşılığı. Formdaki etiketle birebir aynı olmalı; aksi hâlde
 * toast "Fiyat" der, kullanıcı ekranda "Tutar" arar.
 */
const FIELD_LABELS = {
  name: "Modül adı",
  basePrice: "Fiyat",
  vatRate: "KDV oranı",
  lifecycle: "Modül tipi",
  grantsMaxAccounts: "Cari katkısı",
  grantsMaxUsers: "Kullanıcı katkısı",
  grantsCollectionAmount: "Tahsilat tutarı katkısı",
  grantsCollectionCount: "Tahsilat adedi katkısı",
  validityDays: "Geçerlilik süresi",
  sortOrder: "Sıra",
}

const LIFECYCLES = [
  {
    value: "SUBSCRIPTION",
    label: "Aboneliğe bağlı",
    hint: "Hak, mevcut paketin bitiş tarihine kadar geçerlidir; paket yenilendiğinde yeniden alınır.",
  },
  {
    value: "CONSUMABLE",
    label: "Tükenen (kullandıkça biten)",
    hint: "Tahsilat tutarı veya adedi havuzu satılır. Havuz tanımlanmazsa modül asla tükenmez, bu yüzden zorunludur.",
  },
]

/** Numaralı bölüm kartı — paket formundaki desenin aynısı. */
const Section = ({ step, title, description, children }) => (
  <div className="card border-0 rounded-4 mb-3" style={{ boxShadow: "0 .35rem 1rem rgba(10,49,110,.06)" }}>
    <div className="card-body p-4">
      <div className="d-flex align-items-start mb-3">
        <div
          className="d-flex align-items-center justify-content-center flex-shrink-0 me-3"
          style={{ width: 32, height: 32, borderRadius: 999, background: NAVY, color: "#fff", fontWeight: 700, fontSize: ".85rem" }}
        >
          {step}
        </div>
        <div>
          <h6 className="mb-1 fw-semibold text-dark">{title}</h6>
          {description && <div className="text-muted" style={{ fontSize: ".78rem" }}>{description}</div>}
        </div>
      </div>
      {children}
    </div>
  </div>
)

/**
 * Ek modül oluşturma / düzenleme.
 *
 * <h3>Neden alan adları "katkı"</h3>
 * Paket formunda boş bir limit alanı <b>sınırsız</b> demektir; burada boş alan
 * <b>katkı yok</b> demektir. Aynı görünen iki alanın ters anlam taşıması en
 * kolay karıştırılacak yer olduğu için etiketler "+" ile yazılır ve her alanın
 * altında ne olduğu tekrar söylenir.
 *
 * <h3>Düzenleme geçmişi etkilemez</h3>
 * Satın alınmış modüllerin hakları satın alma anında dondurulur; buradaki
 * değişiklik yalnız <i>bundan sonraki</i> satışlara işler. Kullanıcının bunu
 * bilmesi gerekir, bu yüzden formda açıkça yazılıdır.
 */
const AddonModuleForm = () => {
  const navigate = useNavigate()
  const { id } = useParams()
  const isEdit = !!id

  const [module, setModule] = useState(null)
  const [featureCatalog, setFeatureCatalog] = useState([])
  const [integrationCatalog, setIntegrationCatalog] = useState([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)

  document.title = `${isEdit ? "Modülü Düzenle" : "Yeni Ek Modül"} | Tahsilat360`

  const styles = `
    .addon-form .form-check-input:checked { background-color: ${NAVY}; border-color: ${NAVY}; }
    .addon-form .btn-navy { background: ${NAVY}; border-color: ${NAVY}; color: #fff; }
    .addon-form .btn-navy:hover { filter: brightness(1.05); color: #fff; }
    .addon-form .feature-toggle {
      background: #fff; border: 1px solid #dfe3e9; border-radius: .6rem;
      padding: .65rem .9rem; text-align: left; width: 100%;
      transition: border-color .15s, box-shadow .15s;
    }
    .addon-form .feature-toggle:hover { border-color: ${NAVY}; }
    .addon-form .feature-toggle.selected { border-color: ${NAVY}; box-shadow: 0 0 0 2px ${NAVY}22; }
    .addon-form .integration-group + .integration-group { margin-top: .75rem; }
    .addon-form .integration-cat {
      font-size: .66rem; text-transform: uppercase; letter-spacing: .04em;
      color: #97a1ae; font-weight: 700;
    }
    .addon-form .integration-all { font-size: .72rem; text-decoration: none; color: ${NAVY}; font-weight: 600; }
    .addon-form .integration-row {
      border: 1px solid #eef1f5; border-radius: .5rem; padding: .35rem .55rem;
      cursor: pointer; font-size: .82rem;
    }
    .addon-form .integration-row:hover { background: #f6f8fb; }
    .addon-form .integration-row.is-selected { background: #f2f6fc; border-color: #dbe4f0; }
    .addon-form .integration-row label { cursor: pointer; }
    .addon-form .sticky-actions {
      position: sticky; bottom: 0; background: rgba(255,255,255,.94);
      backdrop-filter: blur(4px); padding: .85rem 0; border-top: 1px solid #edf0f4;
    }
  `

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        setLoading(true)
        const [featuresRes, integrationsRes, moduleRes] = await Promise.all([
          adminGetAddonFeatures(),
          adminGetPlanIntegrations(),
          isEdit ? adminGetAddonModule(id) : Promise.resolve(null),
        ])
        if (cancelled) return
        setFeatureCatalog(featuresRes.data || [])
        setIntegrationCatalog(integrationsRes.data || [])
        if (moduleRes?.data) setModule(moduleRes.data)
      } catch (err) {
        if (!cancelled) setError(err.message)
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => { cancelled = true }
  }, [id, isEdit])

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      name: module?.name || "",
      description: module?.description || "",
      lifecycle: module?.lifecycle || "SUBSCRIPTION",
      basePrice: module?.basePrice ?? "",
      vatRate: module?.vatRate ?? 20,
      vatIncluded: module?.vatIncluded ?? false,
      grantsMaxAccounts: module?.grantsMaxAccounts ?? "",
      grantsMaxUsers: module?.grantsMaxUsers ?? "",
      grantsCollectionAmount: module?.grantsCollectionAmount ?? "",
      grantsCollectionCount: module?.grantsCollectionCount ?? "",
      validityDays: module?.validityDays ?? "",
      moduleFeatures: module?.moduleFeatures || [],
      moduleIntegrations: module?.moduleIntegrations || [],
      active: module?.active ?? true,
      visibleToCustomers: module?.visibleToCustomers ?? true,
      sortOrder: module?.sortOrder ?? 0,
    },
    validationSchema: Yup.object({
      name: Yup.string().trim().required("Modül adı zorunludur"),
      basePrice: Yup.number().typeError("Geçerli bir fiyat girin").min(0, "Fiyat negatif olamaz").required("Fiyat zorunludur"),
      vatRate: Yup.number().typeError("Geçerli bir oran girin").min(0).max(100),
      grantsMaxAccounts: Yup.number().transform((v, o) => (o === "" ? null : v)).nullable().positive("0'dan büyük olmalı"),
      grantsMaxUsers: Yup.number().transform((v, o) => (o === "" ? null : v)).nullable().positive("0'dan büyük olmalı"),
      grantsCollectionAmount: Yup.number().transform((v, o) => (o === "" ? null : v)).nullable().positive("0'dan büyük olmalı"),
      grantsCollectionCount: Yup.number().transform((v, o) => (o === "" ? null : v)).nullable().positive("0'dan büyük olmalı"),
      validityDays: Yup.number().transform((v, o) => (o === "" ? null : v)).nullable().positive("0'dan büyük olmalı"),
    }),
    onSubmit: async (values) => {
      const num = (v) => (v === "" || v == null ? null : Number(v))
      const payload = {
        name: values.name.trim(),
        description: values.description || null,
        lifecycle: values.lifecycle,
        basePrice: Number(values.basePrice),
        vatRate: Number(values.vatRate),
        vatIncluded: !!values.vatIncluded,
        grantsMaxAccounts: num(values.grantsMaxAccounts),
        grantsMaxUsers: num(values.grantsMaxUsers),
        grantsCollectionAmount: num(values.grantsCollectionAmount),
        grantsCollectionCount: num(values.grantsCollectionCount),
        // Geçerlilik süresi yalnız tükenen modülde anlamlı; aboneliğe bağlı
        // modülün süresi zaten aboneliğin süresidir (backend de böyle davranır).
        validityDays: values.lifecycle === "CONSUMABLE" ? num(values.validityDays) : null,
        moduleFeatures: values.moduleFeatures,
        moduleIntegrations: values.moduleIntegrations,
        active: !!values.active,
        visibleToCustomers: !!values.visibleToCustomers,
        sortOrder: num(values.sortOrder) ?? 0,
      }

      try {
        setSaving(true)
        const res = isEdit
          ? await adminUpdateAddonModule(id, payload)
          : await adminCreateAddonModule(payload)
        toast.success(res?.message || (isEdit ? "Modül güncellendi" : "Modül oluşturuldu"))
        navigate("/admin/addon-modules")
      } catch (err) {
        // Backend doğrulamaları (havuzsuz tükenen modül, hiçbir şey vermeyen
        // modül, isim çakışması) tek tek mesajla döner.
        toast.error(err.message)
      } finally {
        setSaving(false)
      }
    },
  })

  /**
   * Kaydet'e basıldığında çalışır. Formik'in kendi {@code handleSubmit}'i
   * doğrulama düşünce {@code onSubmit}'i hiç çağırmaz; sonuç olarak hiçbir
   * bildirim çıkmaz ve alan hatası sayfanın görünmeyen bir yerinde kalırsa
   * kullanıcı "butona bastım, bir şey olmadı" der. Bu yüzden doğrulamayı
   * elle çalıştırıp eksikleri hem toast'ta adlandırıyor hem de ilk hatalı
   * alana kaydırıp odaklanıyoruz.
   */
  const submitWithFeedback = async () => {
    const errors = await formik.validateForm()
    const fields = Object.keys(errors)

    if (fields.length === 0) {
      formik.handleSubmit()
      return
    }

    // Formik yalnız dokunulmuş alanların hatasını gösterir; kullanıcı hiçbir
    // alana dokunmadan kaydet'e bastıysa hepsini dokunulmuş sayalım.
    formik.setTouched(
      Object.keys(formik.values).reduce((acc, key) => ({ ...acc, [key]: true }), {}),
      false,
    )

    const named = fields.map((f) => FIELD_LABELS[f] || f)
    toast.error(
      named.length === 1
        ? `${named[0]}: ${errors[fields[0]]}`
        : `Eksik veya hatalı alanlar: ${named.join(", ")}`,
    )

    const first = document.querySelector(`[name="${fields[0]}"]`)
    if (first) {
      first.scrollIntoView({ behavior: "smooth", block: "center" })
      first.focus({ preventScroll: true })
    }
  }

  const toggleFeature = (code) => {
    const current = formik.values.moduleFeatures || []
    formik.setFieldValue(
      "moduleFeatures",
      current.includes(code) ? current.filter((c) => c !== code) : [...current, code]
    )
  }

  const toggleIntegration = (code) => {
    const current = formik.values.moduleIntegrations || []
    formik.setFieldValue(
      "moduleIntegrations",
      current.includes(code) ? current.filter((c) => c !== code) : [...current, code]
    )
  }

  const toggleIntegrationCategory = (category) => {
    const current = formik.values.moduleIntegrations || []
    const codes = integrationCatalog.filter((i) => i.category === category).map((i) => i.code)
    const allSelected = codes.every((c) => current.includes(c))
    formik.setFieldValue(
      "moduleIntegrations",
      allSelected ? current.filter((c) => !codes.includes(c)) : [...new Set([...current, ...codes])]
    )
  }

  const selected = formik.values.moduleFeatures || []
  const selectedIntegrations = formik.values.moduleIntegrations || []
  const integrationGroups = integrationCatalog.reduce((groups, i) => {
    let g = groups.find((x) => x.category === i.category)
    if (!g) { g = { category: i.category, items: [] }; groups.push(g) }
    g.items.push(i)
    return groups
  }, [])
  const categoryLabel = (c) => (c === "Muhasebe" ? "Muhasebe / ERP" : c)
  const isConsumable = formik.values.lifecycle === "CONSUMABLE"

  const base = Number(formik.values.basePrice) || 0
  const rate = Number(formik.values.vatRate) || 0
  const vatAmount = formik.values.vatIncluded
    ? (base * rate) / (rate + 100)
    : (base * rate) / 100
  const total = formik.values.vatIncluded ? base : base + vatAmount
  const fmt = (v) => new Intl.NumberFormat("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(v) + " ₺"

  const grantsNothing =
    selected.length === 0 &&
    selectedIntegrations.length === 0 &&
    !formik.values.grantsMaxAccounts &&
    !formik.values.grantsMaxUsers &&
    !formik.values.grantsCollectionAmount &&
    !formik.values.grantsCollectionCount

  const consumableWithoutPool =
    isConsumable && !formik.values.grantsCollectionAmount && !formik.values.grantsCollectionCount

  if (loading) {
    return (
      <div className="page-content">
        <div className="container-fluid text-center p-5">
          <Spinner size="lg" color="primary" />
          <div className="mt-3 text-muted">Form yükleniyor...</div>
        </div>
      </div>
    )
  }

  return (
    <div className="page-content addon-form">
      <style>{styles}</style>
      <div className="container-fluid">
        <div className="d-flex align-items-center mb-3">
          <BackButton />
          <h5 className="mb-0 ms-2 fw-semibold" style={{ color: NAVY }}>
            {isEdit ? "Ek Modülü Düzenle" : "Yeni Ek Modül"}
          </h5>
        </div>

        {error && <Alert color="danger">{error}</Alert>}

        {isEdit && (
          <Alert color="info" className="border-0" style={{ background: "#EBF0F7", color: NAVY }}>
            <i className="bx bx-lock-alt me-1" />
            Buradaki değişiklikler <strong>yalnız bundan sonraki satışlara</strong> işler.
            Modülü daha önce almış firmaların hakları satın alma anında dondurulmuştur ve değişmez.
          </Alert>
        )}

        <Row>
          <Col xl={8}>
            <Section step={1} title="Kimlik" description="Modülün vitrinde ve panelde görünen adı.">
              <Row>
                <Col md={8}>
                  <FormGroup>
                    <Label>Modül Adı</Label>
                    <Input
                      name="name"
                      value={formik.values.name}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      invalid={formik.touched.name && !!formik.errors.name}
                      placeholder="Örn: 250 Cari Paketi"
                    />
                    <FormFeedback>{formik.errors.name}</FormFeedback>
                  </FormGroup>
                </Col>
                <Col md={4}>
                  <FormGroup>
                    <Label>Sıralama</Label>
                    <Input type="number" name="sortOrder" value={formik.values.sortOrder} onChange={formik.handleChange} />
                  </FormGroup>
                </Col>
              </Row>
              <FormGroup className="mb-0">
                <Label>Açıklama</Label>
                <Input
                  type="textarea"
                  name="description"
                  rows={2}
                  maxLength={500}
                  value={formik.values.description}
                  onChange={formik.handleChange}
                  placeholder="Müşterinin vitrinde göreceği kısa açıklama"
                />
              </FormGroup>
            </Section>

            <Section step={2} title="Modül Tipi" description="Hakkın ne kadar yaşayacağını belirler.">
              {LIFECYCLES.map((lc) => (
                <div key={lc.value} className="form-check mb-2">
                  <input
                    type="radio"
                    className="form-check-input"
                    id={`lc-${lc.value}`}
                    name="lifecycle"
                    value={lc.value}
                    checked={formik.values.lifecycle === lc.value}
                    onChange={formik.handleChange}
                  />
                  <label className="form-check-label fw-semibold" htmlFor={`lc-${lc.value}`}>{lc.label}</label>
                  <div className="text-muted" style={{ fontSize: ".74rem" }}>{lc.hint}</div>
                </div>
              ))}

              {isConsumable && (
                <FormGroup className="mt-3 mb-0" style={{ maxWidth: 220 }}>
                  <Label>Geçerlilik süresi (gün)</Label>
                  <Input
                    type="number"
                    name="validityDays"
                    min={1}
                    placeholder="Süresiz"
                    value={formik.values.validityDays}
                    onChange={formik.handleChange}
                  />
                  <div className="text-muted" style={{ fontSize: ".72rem" }}>
                    Boş = havuz tükenene kadar geçerli
                  </div>
                </FormGroup>
              )}
            </Section>

            <Section step={3} title="Fiyat" description="Karttan çekilecek tutar KDV dâhil hesaplanır.">
              <Row>
                <Col md={4}>
                  <FormGroup>
                    <Label>Baz Fiyat (₺)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      min={0}
                      name="basePrice"
                      value={formik.values.basePrice}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      invalid={formik.touched.basePrice && !!formik.errors.basePrice}
                    />
                    <FormFeedback>{formik.errors.basePrice}</FormFeedback>
                  </FormGroup>
                </Col>
                <Col md={4}>
                  <FormGroup>
                    <Label>KDV Oranı (%)</Label>
                    <Input type="number" name="vatRate" min={0} max={100} value={formik.values.vatRate} onChange={formik.handleChange} />
                  </FormGroup>
                </Col>
                <Col md={4}>
                  <FormGroup>
                    <Label>KDV</Label>
                    <div className="form-check mt-2">
                      <input
                        type="checkbox"
                        className="form-check-input"
                        id="vatIncluded"
                        name="vatIncluded"
                        checked={formik.values.vatIncluded}
                        onChange={formik.handleChange}
                      />
                      <label className="form-check-label" htmlFor="vatIncluded">Fiyata dâhil</label>
                    </div>
                  </FormGroup>
                </Col>
              </Row>
              <div className="bg-light rounded-3 p-3">
                <div className="d-flex justify-content-between" style={{ fontSize: ".85rem" }}>
                  <span className="text-muted">KDV tutarı</span>
                  <span>{fmt(vatAmount)}</span>
                </div>
                <div className="d-flex justify-content-between fw-semibold mt-1">
                  <span>Tahsil edilecek toplam</span>
                  <span style={{ color: NAVY }}>{fmt(total)}</span>
                </div>
              </div>
              {base === 0 && (
                <div className="text-muted mt-2" style={{ fontSize: ".74rem" }}>
                  <i className="bx bx-info-circle me-1" />
                  Sıfır fiyatlı modül satın alınamaz; yalnız yönetici ataması ile verilebilir.
                </div>
              )}
            </Section>

            <Section
              step={4}
              title="Limit Katkıları"
              description="Boş bırakılan alan “sınırsız” değil “katkı yok” demektir. Paket limiti sınırsızsa katkı eklenmez."
            >
              <Row>
                <Col md={6}>
                  <FormGroup>
                    <Label>+ Cari / Bayi</Label>
                    <Input type="number" min={1} name="grantsMaxAccounts" placeholder="Katkı yok"
                      value={formik.values.grantsMaxAccounts} onChange={formik.handleChange} />
                  </FormGroup>
                </Col>
                <Col md={6}>
                  <FormGroup>
                    <Label>+ Kullanıcı</Label>
                    <Input type="number" min={1} name="grantsMaxUsers" placeholder="Katkı yok"
                      value={formik.values.grantsMaxUsers} onChange={formik.handleChange} />
                  </FormGroup>
                </Col>
                <Col md={6}>
                  <FormGroup className="mb-md-0">
                    <Label>+ Tahsilat Tutarı (₺)</Label>
                    <Input type="number" step="0.01" min={1} name="grantsCollectionAmount" placeholder="Katkı yok"
                      value={formik.values.grantsCollectionAmount} onChange={formik.handleChange} />
                  </FormGroup>
                </Col>
                <Col md={6}>
                  <FormGroup className="mb-0">
                    <Label>+ Tahsilat Adedi</Label>
                    <Input type="number" min={1} name="grantsCollectionCount" placeholder="Katkı yok"
                      value={formik.values.grantsCollectionCount} onChange={formik.handleChange} />
                  </FormGroup>
                </Col>
              </Row>

              {consumableWithoutPool && (
                <div className="mt-3 p-3 rounded-3" style={{ background: "#FFF4E5", border: "1px solid #FFD8A8" }}>
                  <i className="bx bx-error me-1" style={{ color: "#B86E00" }} />
                  <span style={{ fontSize: ".8rem" }}>
                    Tükenen modülde tahsilat tutarı veya adedi katkısı <strong>zorunludur</strong>;
                    aksi hâlde tükenecek bir hak olmaz.
                  </span>
                </div>
              )}
            </Section>

            <Section
              step={5}
              title="Özellikler ve Entegrasyonlar"
              description="Modülün paketin üstüne açtığı özellikler ve entegrasyonlar. Modüller yalnız ekler, asla hak geri almaz."
            >
              {featureCatalog.length === 0 ? (
                <div className="text-muted" style={{ fontSize: ".82rem" }}>Özellik kataloğu boş.</div>
              ) : (
                <Row>
                  {featureCatalog.map((f) => {
                    const isOn = selected.includes(f.code)
                    return (
                      <Col md={6} key={f.code} className="mb-2">
                        <button
                          type="button"
                          className={`feature-toggle ${isOn ? "selected" : ""}`}
                          onClick={() => toggleFeature(f.code)}
                        >
                          <div className="d-flex align-items-start">
                            <input type="checkbox" className="form-check-input me-2 mt-1" checked={isOn} readOnly />
                            <div>
                              <div className="fw-semibold" style={{ fontSize: ".85rem" }}>{f.label || f.code}</div>
                              {f.description && (
                                <div className="text-muted" style={{ fontSize: ".72rem" }}>{f.description}</div>
                              )}
                            </div>
                          </div>
                        </button>
                      </Col>
                    )
                  })}
                </Row>
              )}
              <div className="text-muted mt-2" style={{ fontSize: ".78rem" }}>
                {selected.length} özellik seçili
              </div>

              <div className="mt-4">
                <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-1">
                  <span className="d-flex align-items-center gap-2">
                    <i className="bx bx-plug" style={{ color: NAVY }} />
                    <span className="fw-semibold">Entegrasyonlar</span>
                  </span>
                  <span className="text-muted" style={{ fontSize: ".75rem" }}>
                    {selectedIntegrations.length} / {integrationCatalog.length} seçili
                  </span>
                </div>
                <div className="text-muted mb-2" style={{ fontSize: ".72rem" }}>
                  Modülü alan firmada, paketinde olmasa da seçilen entegrasyonlar açılır.
                </div>
                {integrationCatalog.length === 0 ? (
                  <div className="text-muted small">Entegrasyon kataloğu yüklenemedi.</div>
                ) : (
                  integrationGroups.map((g) => {
                    const codes = g.items.map((i) => i.code)
                    const allSelected = codes.every((c) => selectedIntegrations.includes(c))
                    return (
                      <div key={g.category} className="integration-group">
                        <div className="d-flex align-items-center justify-content-between mb-1">
                          <span className="integration-cat">{categoryLabel(g.category)}</span>
                          <button
                            type="button"
                            className="btn btn-link p-0 integration-all"
                            onClick={() => toggleIntegrationCategory(g.category)}
                          >
                            {allSelected ? "Tümünü kaldır" : "Tümünü seç"}
                          </button>
                        </div>
                        <Row className="g-2">
                          {g.items.map((i) => {
                            const checked = selectedIntegrations.includes(i.code)
                            return (
                              <Col xs={6} md={4} key={i.code}>
                                <div
                                  className={`integration-row d-flex align-items-center gap-2 ${checked ? "is-selected" : ""}`}
                                  onClick={() => toggleIntegration(i.code)}
                                >
                                  <input
                                    type="checkbox"
                                    className="form-check-input mt-0"
                                    id={`intg-${i.code}`}
                                    checked={checked}
                                    onChange={() => toggleIntegration(i.code)}
                                    onClick={(e) => e.stopPropagation()}
                                  />
                                  <label
                                    className="form-check-label mb-0"
                                    htmlFor={`intg-${i.code}`}
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    {i.label}
                                  </label>
                                </div>
                              </Col>
                            )
                          })}
                        </Row>
                      </div>
                    )
                  })
                )}
              </div>

              {grantsNothing && (
                <div className="mt-3 p-3 rounded-3" style={{ background: "#FFF4E5", border: "1px solid #FFD8A8" }}>
                  <i className="bx bx-error me-1" style={{ color: "#B86E00" }} />
                  <span style={{ fontSize: ".8rem" }}>
                    Modül en az bir özellik veya entegrasyon açmalı ya da bir limite katkı vermelidir; aksi hâlde
                    satın alan firmanın hiçbir şeyi değişmez.
                  </span>
                </div>
              )}
            </Section>
          </Col>

          <Col xl={4}>
            <div className="card border-0 rounded-4" style={{ boxShadow: "0 .35rem 1rem rgba(10,49,110,.06)" }}>
              <div className="card-body p-4">
                <h6 className="fw-semibold mb-3">Yayın Durumu</h6>

                <div className="form-check mb-3">
                  <input type="checkbox" className="form-check-input" id="active" name="active"
                    checked={formik.values.active} onChange={formik.handleChange} />
                  <label className="form-check-label" htmlFor="active">Aktif</label>
                  <div className="text-muted" style={{ fontSize: ".72rem" }}>
                    Pasif modül satılamaz; daha önce satılmış haklar etkilenmez.
                  </div>
                </div>

                <div className="form-check mb-0">
                  <input type="checkbox" className="form-check-input" id="visibleToCustomers" name="visibleToCustomers"
                    checked={formik.values.visibleToCustomers} onChange={formik.handleChange} />
                  <label className="form-check-label" htmlFor="visibleToCustomers">Vitrinde görünür</label>
                  <div className="text-muted" style={{ fontSize: ".72rem" }}>
                    Kapalıysa firmalar satın alamaz; yalnız yönetici ataması ile dağıtılır.
                  </div>
                </div>
              </div>
            </div>
          </Col>
        </Row>

        <div className="sticky-actions">
          <div className="d-flex justify-content-end gap-2">
            <Button color="light" onClick={() => navigate("/admin/addon-modules")} disabled={saving}>
              İptal
            </Button>
            <Button className="btn-navy" onClick={submitWithFeedback} disabled={saving}>
              {saving ? <Spinner size="sm" /> : isEdit ? "Değişiklikleri Kaydet" : "Modülü Oluştur"}
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default AddonModuleForm
